import type {FetchWrapper} from '@/ts/api/fetch-wrapper';
import type {
  AnalyticsReport,
  ContentAnalyticsReport,
  DemographicsReport,
  GetAnalyticsReportParams,
  GetRetentionParams,
  RetentionRateWidget,
} from '@/ts/types/dto/analytics-dto';
import type {LicenseDTO} from '@/ts/types/dto/sign-in-dto';

interface LicensesResponse {
  licenses: LicenseDTO[];
  totalCount: number;
}

/** API module for admin endpoints: analytics reports and license management */
export class AdminApi {
  public constructor(private readonly fetch: FetchWrapper) {
  }

  public async getAnalyticsReport(params: GetAnalyticsReportParams): Promise<AnalyticsReport> {
    return this.fetch.get({
      url: '/report',
      queryParams: {
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.licenseId && {licenseId: params.licenseId}),
      },
    });
  }

  public async getContentAnalytics(params: GetAnalyticsReportParams): Promise<ContentAnalyticsReport> {
    return this.fetch.get({
      url: '/content',
      queryParams: {
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.licenseId && {licenseId: params.licenseId}),
      },
    });
  }

  public async getDemographics(params: GetAnalyticsReportParams): Promise<DemographicsReport> {
    return this.fetch.get({
      url: '/demographics',
      queryParams: {
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.licenseId && {licenseId: params.licenseId}),
      },
    });
  }

  public async downloadReport(params: {startDate?: string; endDate?: string; licenseId?: string}): Promise<{blob: Blob; filename: string}> {
    return this.fetch.getBlob({
      url: '/report/export',
      queryParams: {
        ...(params.startDate && {startDate: params.startDate}),
        ...(params.endDate && {endDate: params.endDate}),
        ...(params.licenseId && {licenseId: params.licenseId}),
      },
    });
  }

  public async getRetention(params: GetRetentionParams = {}): Promise<RetentionRateWidget> {
    return this.fetch.get({
      url: '/retention',
      queryParams: {
        ...(params.licenseId && {licenseId: params.licenseId}),
      },
    });
  }

  public async getLicenses(): Promise<LicenseDTO[]> {
    const response: LicensesResponse = await this.fetch.get({
      url: '/licenses',
    });
    return response.licenses;
  }
}
